import type { ButtonHTMLAttributes } from 'react';
import {
  CheckCircle2,
  CircleSlash,
  Copy,
  Pause,
  Play,
  Save,
  SquarePen,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * Shared icon-only actions (tooltips via title + aria-label).
 * Use for edit/save/delete/approve row & detail controls — not primary
 * create CTAs ("New expense", "Log time", …).
 */
export type IconActionKind =
  | 'edit'
  | 'save'
  | 'delete'
  | 'cancel'
  | 'approve'
  | 'unapprove'
  | 'pause'
  | 'resume'
  | 'duplicate';

const PRESETS: Record<
  IconActionKind,
  { Icon: LucideIcon; label: string; tone?: 'cyan' | 'danger' }
> = {
  edit: { Icon: SquarePen, label: 'Edit' },
  save: { Icon: Save, label: 'Save', tone: 'cyan' },
  delete: { Icon: Trash2, label: 'Delete', tone: 'danger' },
  cancel: { Icon: X, label: 'Cancel' },
  approve: { Icon: CheckCircle2, label: 'Approve', tone: 'cyan' },
  unapprove: { Icon: CircleSlash, label: 'Unapprove' },
  pause: { Icon: Pause, label: 'Pause' },
  resume: { Icon: Play, label: 'Resume', tone: 'cyan' },
  // Opens the create form pre-filled from this record — nothing is saved
  // until the user saves it.
  duplicate: { Icon: Copy, label: 'Duplicate and edit' },
};

export interface IconActionProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  action: IconActionKind;
  /** Override tooltip / aria-label (defaults to the action name). */
  label?: string;
  iconSize?: number;
}

export function IconAction({
  action,
  label,
  iconSize = 18,
  className = '',
  title,
  type = 'button',
  ...rest
}: IconActionProps): JSX.Element {
  const preset = PRESETS[action];
  const text = label ?? preset.label;
  const toneClass =
    preset.tone === 'cyan'
      ? ' icon-btn-cyan'
      : preset.tone === 'danger'
        ? ' icon-btn-danger'
        : '';
  const Icon = preset.Icon;

  return (
    <button
      type={type}
      className={`icon-btn${toneClass}${className ? ` ${className}` : ''}`}
      title={title ?? text}
      aria-label={rest['aria-label'] ?? text}
      {...rest}
    >
      <Icon size={iconSize} strokeWidth={1.75} aria-hidden />
    </button>
  );
}
